/**
 * ChannelHealthDot
 *
 * Small colored dot shown beside client name:
 * - Green: all channels healthy
 * - Amber: any expiring
 * - Red: any broken
 */

import { useTranslation } from 'react-i18next';
import { Tooltip } from '@/components/ui/Tooltip';
import type { ChannelHealthData } from '@/data/portfolio.types';

interface ChannelHealthDotProps {
  channelHealth: ChannelHealthData;
  showLabel?: boolean;
}

export const ChannelHealthDot = ({ channelHealth, showLabel = false }: ChannelHealthDotProps) => {
  const { t } = useTranslation('agency');
  const { total, broken, expiring } = channelHealth;

  if (total === 0) return null;

  let dotColor: string;
  let label: string;

  if (broken > 0) {
    dotColor = 'bg-coral-500';
    label = t('portfolio_channel_dot_broken', { count: broken });
  } else if (expiring > 0) {
    dotColor = 'bg-peach';
    label = t('portfolio_channel_dot_expiring', { count: expiring });
  } else {
    dotColor = 'bg-primary-400';
    label = t('portfolio_channel_dot_healthy', { count: total });
  }

  return (
    <Tooltip content={label} side="top">
      <span
        className={showLabel ? 'inline-flex items-center gap-1 text-[10px] text-gray-600 border-l border-gray-200 pl-2' : 'inline-flex items-center'}
        aria-label={label}
        role="status"
      >
        <span className={`w-2 h-2 rounded-full inline-block shrink-0 ${dotColor}`} aria-hidden="true" />
        {showLabel && <span>{broken > 0 ? t('portfolio_risk_chip_channels_broken', { count: broken }) : t('portfolio_badge_channel_health', { defaultValue: 'Channel health' })}</span>}
      </span>
    </Tooltip>
  );
};
